import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ChevronLeft, ChevronRight, CalendarRange, Plus, User, Users, Truck, MapPin, Flag, AlertTriangle, RefreshCcw,
  List, BarChart3, Trash2, Copy, CalendarPlus, CheckCircle2, Bus, StickyNote,
} from "lucide-react";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import AddressBlock, { emptyAddr, normAddr } from "@/components/AddressBlock";
import { dateKey } from "@/components/DayNav";
import DriverDetailModal from "@/components/DriverDetailModal";
import VehicleDetailModal from "@/components/VehicleDetailModal";
import { useAuth } from "@/contexts/AuthContext";

const STATUS = ["geplant", "unterwegs", "abgeschlossen", "storniert"];
const STATUS_LABEL = { geplant: "Geplant", unterwegs: "Unterwegs", abgeschlossen: "Abgeschlossen", storniert: "Storniert" };
const WD = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];

const mondayOf = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - dow);
  return x;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const shortDate = (d) => d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
const parseKey = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const addr1 = (a) => {
  if (!a) return "—";
  const s = [a.street, a.house_number].filter(Boolean).join(" ");
  return [s, a.city].filter(Boolean).join(", ") || "—";
};
const firstPickup = (t) => (t.pickups && t.pickups[0]) || t.pickup_address;
const firstDest = (t) => (t.destinations && t.destinations[0]) || t.destination_address;

const emptyTrip = (date) => ({
  pickups: [{ ...emptyAddr }],
  destinations: [{ ...emptyAddr }],
  trip_date: date,
  start_time: "08:00",
  end_time: "",
  driver_id: "",
  vehicle_id: "",
  status: "geplant",
  line_label: "",
  notes: "",
});

// Parse "HH:MM" -> minutes since 00:00. Returns null on invalid.
const toMinutes = (s) => {
  if (!s) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return null;
  const h = parseInt(m[1], 10), mm = parseInt(m[2], 10);
  if (isNaN(h) || isNaN(mm)) return null;
  return Math.max(0, Math.min(24 * 60, h * 60 + mm));
};

// Deterministic color per driver (or fallback by vehicle / "ohne")
const COLOR_PALETTE = [
  { bg: "bg-blue-500/85", border: "border-blue-700", text: "text-white", solid: "bg-blue-500" },
  { bg: "bg-emerald-500/85", border: "border-emerald-700", text: "text-white", solid: "bg-emerald-500" },
  { bg: "bg-amber-500/90", border: "border-amber-700", text: "text-black", solid: "bg-amber-500" },
  { bg: "bg-rose-500/85", border: "border-rose-700", text: "text-white", solid: "bg-rose-500" },
  { bg: "bg-violet-500/85", border: "border-violet-700", text: "text-white", solid: "bg-violet-500" },
  { bg: "bg-cyan-500/85", border: "border-cyan-700", text: "text-white", solid: "bg-cyan-500" },
  { bg: "bg-fuchsia-500/85", border: "border-fuchsia-700", text: "text-white", solid: "bg-fuchsia-500" },
  { bg: "bg-orange-500/90", border: "border-orange-700", text: "text-black", solid: "bg-orange-500" },
  { bg: "bg-teal-500/85", border: "border-teal-700", text: "text-white", solid: "bg-teal-500" },
  { bg: "bg-indigo-500/85", border: "border-indigo-700", text: "text-white", solid: "bg-indigo-500" },
];
const UNASSIGNED_COLOR = { bg: "bg-zinc-300/90 dark:bg-zinc-700/90", border: "border-zinc-500 dark:border-zinc-400", text: "text-zinc-900 dark:text-zinc-100", solid: "bg-zinc-400" };

const colorForTrip = (t) => {
  if (!t.driver_id) return UNASSIGNED_COLOR;
  return colorForDriverId(t.driver_id);
};

const colorForDriverId = (driverId) => {
  if (!driverId) return UNASSIGNED_COLOR;
  let h = 0;
  for (let i = 0; i < driverId.length; i++) h = (h * 31 + driverId.charCodeAt(i)) >>> 0;
  return COLOR_PALETTE[h % COLOR_PALETTE.length];
};

export default function WeeklyPlanning() {
  const { user } = useAuth();
  const canEdit = user.role !== "fahrer";

  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [view, setView] = useState("list"); // "list" | "timeline"
  const snapMin = 15; // fixes Drag-Raster (separate Raster-Auswahl entfernt – Zoom übernimmt die Skalierung)

  // Timeline zoom: visible time span (in minutes) that defines the horizontal scale.
  const ZOOM_OPTIONS = [
    { value: 30, label: "Zoom: 30 Min" },
    { value: 60, label: "Zoom: 1 Std" },
    { value: 360, label: "Zoom: 6 Std" },
    { value: 720, label: "Zoom: 12 Std" },
    { value: 1440, label: "Zoom: 24 Std" },
  ];
  const [zoomMin, setZoomMinState] = useState(() => {
    const saved = parseInt(localStorage.getItem("week-timeline-zoom") || "", 10);
    return [30, 60, 360, 720, 1440].includes(saved) ? saved : 1440;
  });
  const setZoomMin = (v) => { setZoomMinState(v); localStorage.setItem("week-timeline-zoom", String(v)); };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTrip(dateKey(new Date())));

  // Copy-to-other-days state
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySource, setCopySource] = useState(null);
  const [copyDates, setCopyDates] = useState([]);   // array of "YYYY-MM-DD"
  const [copyReset, setCopyReset] = useState(true);
  const [copyBusy, setCopyBusy] = useState(false);

  // Multi-date creation (plan a trip on several days at once) + confirmation
  const [repeatDates, setRepeatDates] = useState([]);   // array of "YYYY-MM-DD" (additional days; base = form.trip_date)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);

  // Detail modals
  const [detailDriverId, setDetailDriverId] = useState(null);
  const [detailVehicleId, setDetailVehicleId] = useState(null);
  const openDriver = (id) => id && setDetailDriverId(id);
  const openVehicle = (id) => id && setDetailVehicleId(id);
  const detailDriver = useMemo(() => drivers.find((d) => d.id === detailDriverId) || null, [drivers, detailDriverId]);
  const detailVehicle = useMemo(() => vehicles.find((v) => v.id === detailVehicleId) || null, [vehicles, detailVehicleId]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const weekEnd = days[6];

  const load = async () => {
    try {
      const [t, d, v, l] = await Promise.all([api.get("/trips"), api.get("/drivers"), api.get("/vehicles"), api.get("/leaves").catch(() => ({ data: [] }))]);
      setTrips(t.data); setDrivers(d.data); setVehicles(v.data); setLeaves(l.data);
    } catch (e) { if (e?.response?.status !== 401) toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Absence lookup: returns "urlaub" | "krank" | null for a driver on a given YYYY-MM-DD.
  const absenceFor = (driverId, dateStr) => {
    if (!driverId || !dateStr) return null;
    const l = leaves.find((x) => x.driver_id === driverId && x.date_from <= dateStr && dateStr <= x.date_to);
    return l ? l.type : null;
  };
  const ABSENCE_LABEL = { urlaub: "Urlaub", krank: "Krank" };

  const byDay = useMemo(() => {
    const map = {};
    days.forEach((d) => { map[dateKey(d)] = []; });
    trips.forEach((t) => {
      if (map[t.trip_date]) map[t.trip_date].push(t);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")));
    return map;
  }, [trips, days]);

  const weekTrips = useMemo(() => days.flatMap((d) => byDay[dateKey(d)] || []), [days, byDay]);
  const unassigned = weekTrips.filter((t) => !t.driver_id).length;

  // Per-driver week summary: trip count + total planned hours (HH:MM minutes accumulated).
  const driverSummary = useMemo(() => {
    const map = new Map();
    // Seed all active drivers so 0-trip drivers are still listed
    for (const dv of drivers) {
      if (!dv.active) continue;
      const name = [dv.firstname, dv.lastname].filter(Boolean).join(" ").trim() || dv.lastname || dv.firstname || "—";
      map.set(dv.id, { driverId: dv.id, name, tripCount: 0, minutes: 0 });
    }
    let unMin = 0, unCount = 0;
    for (const t of weekTrips) {
      const s = toMinutes(t.start_time);
      const en = toMinutes(t.end_time);
      const dur = (s != null && en != null && en > s) ? (en - s) : 0;
      if (!t.driver_id) { unCount += 1; unMin += dur; continue; }
      if (!map.has(t.driver_id)) {
        // Driver may be inactive/deleted; still show using trip's driver_name
        map.set(t.driver_id, { driverId: t.driver_id, name: t.driver_name || "Fahrer", tripCount: 0, minutes: 0 });
      }
      const e = map.get(t.driver_id);
      e.tripCount += 1;
      e.minutes += dur;
    }
    const arr = Array.from(map.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if (unCount > 0) arr.push({ driverId: null, name: "ohne Fahrer", tripCount: unCount, minutes: unMin });
    return arr;
  }, [drivers, weekTrips]);

  const totalPlannedMin = useMemo(() => driverSummary.reduce((acc, d) => acc + d.minutes, 0), [driverSummary]);

  const patchTrip = async (id, patch) => {
    try {
      await api.patch(`/trips/${id}`, patch);
      setTrips((arr) => arr.map((t) => (t.id === id ? { ...t, ...patch, ...enrichLocal(patch) } : t)));
    } catch (e) { toast.error(formatApiError(e)); load(); }
  };
  const enrichLocal = (patch) => {
    const out = {};
    if ("driver_id" in patch) {
      const d = drivers.find((x) => x.id === patch.driver_id);
      out.driver_name = d ? `${d.firstname} ${d.lastname}` : null;
    }
    if ("vehicle_id" in patch) {
      const v = vehicles.find((x) => x.id === patch.vehicle_id);
      out.license_plate = v ? v.license_plate : null;
    }
    return out;
  };

  // Multi-address form helpers
  const updateAddr = (kind, i, addr) => setForm((f) => ({ ...f, [kind]: f[kind].map((a, idx) => idx === i ? addr : a) }));
  const addAddr = (kind) => setForm((f) => ({ ...f, [kind]: [...f[kind], { ...emptyAddr }] }));
  const rmAddr = (kind, i) => setForm((f) => ({ ...f, [kind]: f[kind].length <= 1 ? f[kind] : f[kind].filter((_, idx) => idx !== i) }));

  const openCreate = (d) => {
    setEditing(null);
    setForm(emptyTrip(dateKey(d)));
    setRepeatDates([]);
    setDialogOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    const pickups = (t.pickups && t.pickups.length ? t.pickups : [t.pickup_address]).filter(Boolean).map(normAddr);
    const destinations = (t.destinations && t.destinations.length ? t.destinations : [t.destination_address]).filter(Boolean).map(normAddr);
    setForm({
      ...emptyTrip(t.trip_date),
      ...t,
      pickups: pickups.length ? pickups : [{ ...emptyAddr }],
      destinations: destinations.length ? destinations : [{ ...emptyAddr }],
      driver_id: t.driver_id || "",
      vehicle_id: t.vehicle_id || "",
      line_label: t.line_label || "",
      notes: t.notes || "",
    });
    setDialogOpen(true);
  };

  const buildPayload = (tripDate) => ({
    pickups: form.pickups,
    destinations: form.destinations,
    trip_date: tripDate,
    start_time: form.start_time,
    end_time: form.end_time || null,
    driver_id: form.driver_id || null,
    vehicle_id: form.vehicle_id || null,
    status: form.status,
    line_label: form.line_label || "",
    notes: form.notes || "",
  });

  const validateTrip = () => {
    const anyContent = (a) => a && (a.street || a.city || a.note || a.special || a.house_number || a.zip);
    if (!form.pickups.some(anyContent)) { toast.error("Bitte mindestens eine Abholadresse angeben"); return false; }
    if (!form.destinations.some(anyContent)) { toast.error("Bitte mindestens eine Zieladresse angeben"); return false; }
    if (!form.trip_date) { toast.error("Bitte ein Datum angeben"); return false; }
    return true;
  };

  // All dates for a multi-date creation: base day + selected repeat days (unique, sorted)
  const allTripDates = Array.from(new Set([form.trip_date, ...repeatDates].filter(Boolean))).sort();

  const saveTrip = async () => {
    // editing → single update; creation → go through confirmation step
    if (!validateTrip()) return;
    if (!form.driver_id || !form.vehicle_id) {
      const missing = !form.driver_id && !form.vehicle_id ? "Fahrer und Fahrzeug" : (!form.driver_id ? "Fahrer" : "Fahrzeug");
      toast.warning(`Hinweis: ${missing} nicht zugewiesen – die Fahrt wird trotzdem gespeichert.`);
    }
    if (editing) {
      try {
        await api.patch(`/trips/${editing.id}`, buildPayload(form.trip_date));
        toast.success("Fahrt aktualisiert");
        setDialogOpen(false);
        load();
      } catch (e) { toast.error(formatApiError(e)); }
      return;
    }
    // creation: open review dialog
    setDialogOpen(false);
    setConfirmOpen(true);
  };

  const confirmCreate = async () => {
    if (allTripDates.length === 0) { toast.error("Keine Termine ausgewählt"); return; }
    setConfirmBusy(true);
    let ok = 0;
    const failed = [];
    for (const d of allTripDates) {
      try { await api.post("/trips", buildPayload(d)); ok += 1; }
      catch { failed.push(d); }
    }
    setConfirmBusy(false);
    if (ok > 0) toast.success(`${ok} ${ok === 1 ? "Fahrt" : "Fahrten"} angelegt`);
    if (failed.length) toast.error(`${failed.length} Termin(e) fehlgeschlagen`);
    setConfirmOpen(false);
    setRepeatDates([]);
    load();
  };

  const cancelConfirm = () => {
    // back to the creation form with all entered data intact
    setConfirmOpen(false);
    setDialogOpen(true);
  };

  const deleteTrip = async () => {
    if (!editing) return;
    if (!window.confirm("Fahrt wirklich löschen?")) return;
    try {
      await api.delete(`/trips/${editing.id}`);
      toast.success("Fahrt gelöscht");
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ----- Copy to other days -----
  const openCopy = (t) => {
    setCopySource(t);
    setCopyDates([]);
    setCopyReset(true);
    setDialogOpen(false);
    setCopyOpen(true);
  };
  const toggleCopyDate = (key) =>
    setCopyDates((arr) => (arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key]));

  const doCopy = async () => {
    if (!copySource) return;
    const dates = copyDates.filter((k) => k !== copySource.trip_date);
    if (dates.length === 0) { toast.error("Bitte mindestens einen Zieltag auswählen"); return; }
    setCopyBusy(true);
    try {
      await api.post(`/trips/${copySource.id}/duplicate`, { dates, reset_status: copyReset });
      toast.success(`Fahrt auf ${dates.length} ${dates.length === 1 ? "Tag" : "Tage"} kopiert`);
      setCopyOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCopyBusy(false); }
  };

  const isToday = (d) => dateKey(d) === dateKey(new Date());

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl heading font-semibold flex items-center gap-2"><CalendarRange className="h-6 w-6" /> Wochenplanung</h1>
          <p className="text-sm text-muted-foreground">Alle Fahrten der Woche – als Liste oder als Stunden-Timeline.</p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          {canEdit && (
            <Button size="sm" onClick={() => openCreate(weekStart)} data-testid="week-new-trip">
              <Plus className="h-4 w-4 mr-1" /> Neue Fahrt
            </Button>
          )}
          <span className="text-muted-foreground">Fahrten: <span className="font-semibold text-foreground" data-testid="week-total">{weekTrips.length}</span></span>
          {unassigned > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/60 dark:text-red-200" data-testid="week-unassigned">
              <AlertTriangle className="h-3 w-3" /> {unassigned} ohne Fahrer
            </span>
          )}
        </div>
      </div>

      {/* Week navigation + view toggle */}
      <Card className="p-3 flex items-center gap-2 flex-wrap" data-testid="week-nav">
        <Button size="icon" variant="outline" onClick={() => setWeekStart(addDays(weekStart, -7))} aria-label="Vorherige Woche" data-testid="week-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="min-w-[240px] justify-start font-normal" data-testid="week-calendar-trigger">
              <CalendarRange className="h-4 w-4 mr-2 text-muted-foreground" />
              {weekStart.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })} – {weekEnd.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={weekStart} onSelect={(d) => d && setWeekStart(mondayOf(d))} initialFocus />
          </PopoverContent>
        </Popover>
        <Button size="icon" variant="outline" onClick={() => setWeekStart(addDays(weekStart, 7))} aria-label="Nächste Woche" data-testid="week-next"><ChevronRight className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" onClick={() => setWeekStart(mondayOf(new Date()))} data-testid="week-today">Aktuelle Woche</Button>

        <div className="ml-auto flex items-center gap-2">
          {view === "timeline" && (
            <Select value={String(zoomMin)} onValueChange={(v) => setZoomMin(parseInt(v, 10))}>
              <SelectTrigger className="h-9 w-[130px] text-xs" data-testid="week-timeline-zoom"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ZOOM_OPTIONS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <ToggleGroup type="single" value={view} onValueChange={(v) => v && setView(v)} data-testid="week-view-toggle">
            <ToggleGroupItem value="list" aria-label="Liste" data-testid="week-view-list" className="gap-1 text-xs"><List className="h-4 w-4" /> Liste</ToggleGroupItem>
            <ToggleGroupItem value="timeline" aria-label="Timeline" data-testid="week-view-timeline" className="gap-1 text-xs"><BarChart3 className="h-4 w-4" /> Timeline</ToggleGroupItem>
          </ToggleGroup>
          <Button variant="ghost" size="icon" onClick={load} aria-label="Aktualisieren" data-testid="week-refresh"><RefreshCcw className="h-4 w-4" /></Button>
        </div>
      </Card>

      {/* Per-driver week summary (visible in both list and timeline views) */}
      <WeekDriverSummary
        items={driverSummary}
        totalPlannedMin={totalPlannedMin}
        totalTrips={weekTrips.length}
        onOpenDriver={openDriver}
      />

      {view === "list" ? (
        <ListBoard
          days={days}
          byDay={byDay}
          isToday={isToday}
          drivers={drivers}
          vehicles={vehicles}
          openCreate={openCreate}
          openEdit={openEdit}
          openCopy={openCopy}
          patchTrip={patchTrip}
          canEdit={canEdit}
          absenceFor={absenceFor}
          ABSENCE_LABEL={ABSENCE_LABEL}
        />
      ) : (
        <TimelineBoard
          days={days}
          byDay={byDay}
          isToday={isToday}
          openCreate={openCreate}
          openEdit={openEdit}
          patchTrip={patchTrip}
          canEdit={canEdit}
          snapMin={snapMin}
          zoomMin={zoomMin}
          openDriver={openDriver}
        />
      )}

      {/* Create / edit dialog with multi-address support */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Fahrt bearbeiten" : "Fahrt planen"}</DialogTitle>
            <DialogDescription>Beliebig viele Abhol- und Zieladressen mit eigener Bemerkung und Besonderheiten erfassen.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {/* Pickups */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold heading text-muted-foreground uppercase tracking-wider flex items-center gap-1"><MapPin className="h-4 w-4" /> Abholadressen</h3>
                {canEdit && <Button type="button" size="sm" variant="outline" onClick={() => addAddr("pickups")} data-testid="week-add-pickup"><Plus className="h-3.5 w-3.5 mr-1" /> Weitere Abholadresse</Button>}
              </div>
              <div className="space-y-3">
                {form.pickups.map((a, i) => (
                  <AddressBlock key={i} index={i} value={a} prefix="week-pickup" removable={form.pickups.length > 1}
                    onRemove={() => rmAddr("pickups", i)} onChange={(v) => updateAddr("pickups", i, v)} />
                ))}
              </div>
            </section>

            {/* Destinations */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold heading text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Flag className="h-4 w-4" /> Zieladressen</h3>
                {canEdit && <Button type="button" size="sm" variant="outline" onClick={() => addAddr("destinations")} data-testid="week-add-destination"><Plus className="h-3.5 w-3.5 mr-1" /> Weitere Zieladresse</Button>}
              </div>
              <div className="space-y-3">
                {form.destinations.map((a, i) => (
                  <AddressBlock key={i} index={i} value={a} prefix="week-dest" removable={form.destinations.length > 1}
                    onRemove={() => rmAddr("destinations", i)} onChange={(v) => updateAddr("destinations", i, v)} />
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold heading mb-2 text-muted-foreground uppercase tracking-wider">Fahrtinformationen</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Datum</Label><Input type="date" value={form.trip_date} onChange={(e) => setForm({ ...form, trip_date: e.target.value })} data-testid="week-trip-date" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Start</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="week-trip-start" /></div>
                  <div><Label>Ende</Label><Input type="time" value={form.end_time || ""} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="week-trip-end" /></div>
                </div>
                <div>
                  <Label>Fahrer</Label>
                  <Select value={form.driver_id || "none"} onValueChange={(v) => setForm({ ...form, driver_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="week-trip-driver"><SelectValue placeholder="Fahrer wählen" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— keiner —</SelectItem>
                      {drivers.filter((d) => d.active).map((d) => {
                        const ab = absenceFor(d.id, form.trip_date);
                        return <SelectItem key={d.id} value={d.id} className={ab ? "text-amber-600 dark:text-amber-400" : ""}>{d.firstname} {d.lastname}{ab ? ` · ${ABSENCE_LABEL[ab]}` : ""}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fahrzeug</Label>
                  <Select value={form.vehicle_id || "none"} onValueChange={(v) => setForm({ ...form, vehicle_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="week-trip-vehicle"><SelectValue placeholder="Fahrzeug wählen" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— keines —</SelectItem>
                      {vehicles.filter((v) => v.active).map((v) => <SelectItem key={v.id} value={v.id}>{v.license_plate}{v.vehicle_type ? ` · ${v.vehicle_type}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="week-trip-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Linien Bezeichnung</Label>
                  <Input value={form.line_label || ""} onChange={(e) => setForm({ ...form, line_label: e.target.value })} placeholder="z.B. Linie 12" data-testid="week-trip-line" />
                </div>
                <div className="col-span-2">
                  <Label>Bemerkungen (Fahrt)</Label>
                  <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="week-trip-notes" />
                </div>
              </div>
            </section>

            {/* Multi-date / recurring planning (only when creating) */}
            {!editing && (
              <section data-testid="week-repeat-section">
                <h3 className="text-sm font-semibold heading mb-1 text-muted-foreground uppercase tracking-wider flex items-center gap-1"><CalendarPlus className="h-4 w-4" /> Weitere Termine (optional)</h3>
                <p className="text-xs text-muted-foreground mb-2">
                  Wähle im Kalender beliebige zusätzliche Tage, an denen diese Fahrt ebenfalls stattfindet. Der Basistag
                  {form.trip_date ? <> (<span className="font-medium text-foreground">{parseKey(form.trip_date).toLocaleDateString("de-DE")}</span>)</> : null} ist immer enthalten (blau umrandet).
                </p>
                <div className="rounded-md border border-border p-2 inline-block">
                  <Calendar
                    mode="multiple"
                    selected={repeatDates.map(parseKey)}
                    onSelect={(ds) => setRepeatDates((ds || []).map(dateKey))}
                    modifiers={form.trip_date ? { base: parseKey(form.trip_date) } : undefined}
                    modifiersClassNames={{ base: "ring-2 ring-blue-500 rounded-md" }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2" data-testid="week-repeat-count">
                  <span className="font-semibold text-foreground">{allTripDates.length}</span> Termin{allTripDates.length === 1 ? "" : "e"} insgesamt ausgewählt.
                </p>
              </section>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            {editing && canEdit && (
              <Button variant="outline" className="text-red-600 hover:text-red-700 mr-auto" onClick={deleteTrip} data-testid="week-trip-delete">
                <Trash2 className="h-4 w-4 mr-1" /> Löschen
              </Button>
            )}
            {editing && canEdit && (
              <Button variant="outline" onClick={() => openCopy(editing)} data-testid="week-trip-copy">
                <Copy className="h-4 w-4 mr-1" /> Kopieren
              </Button>
            )}
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            {canEdit && <Button onClick={saveTrip} data-testid="week-trip-save">{editing ? "Speichern" : "Weiter zur Kontrolle"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review / confirm dialog before creating (single or multi-date) */}
      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!o) cancelConfirm(); }}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="week-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> Fahrten bestätigen</DialogTitle>
            <DialogDescription>Bitte die Angaben prüfen. Erst nach Bestätigung werden die Fahrten angelegt.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="rounded-md border border-border p-3 space-y-1.5">
              <div className="flex gap-2"><MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" /><span><span className="text-muted-foreground">Von:</span> <span className="font-medium">{addr1(firstPickup({ pickups: form.pickups })) || "—"}</span></span></div>
              <div className="flex gap-2"><Flag className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" /><span><span className="text-muted-foreground">Nach:</span> <span className="font-medium">{addr1(firstDest({ destinations: form.destinations })) || "—"}</span></span></div>
              {(form.pickups.length > 1 || form.destinations.length > 1) && (
                <div className="text-xs text-muted-foreground">+ weitere Adressen ({form.pickups.length} Abhol-, {form.destinations.length} Zieladressen)</div>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
                <span><span className="text-muted-foreground">Zeit:</span> <span className="font-medium tabular-nums">{form.start_time}{form.end_time ? `–${form.end_time}` : ""}</span></span>
                <span><span className="text-muted-foreground">Fahrer:</span> <span className="font-medium">{drivers.find((d) => d.id === form.driver_id) ? `${drivers.find((d) => d.id === form.driver_id).firstname} ${drivers.find((d) => d.id === form.driver_id).lastname}` : "—"}</span></span>
                <span><span className="text-muted-foreground">Fahrzeug:</span> <span className="font-medium">{vehicles.find((v) => v.id === form.vehicle_id)?.license_plate || "—"}</span></span>
                <span><span className="text-muted-foreground">Linie:</span> <span className="font-medium">{form.line_label || "—"}</span></span>
              </div>
              {form.notes ? <div className="text-xs"><span className="text-muted-foreground">Bemerkung:</span> {form.notes}</div> : null}
            </div>
            <div>
              <div className="font-semibold mb-1.5 flex items-center gap-1"><CalendarRange className="h-4 w-4" /> Termine ({allTripDates.length})</div>
              <ul className="space-y-1 max-h-48 overflow-y-auto" data-testid="week-confirm-dates">
                {allTripDates.map((k) => (
                  <li key={k} className="flex items-center justify-between rounded-md bg-muted/50 px-2.5 py-1.5">
                    <span className="font-medium tabular-nums">{parseKey(k).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" })}</span>
                    {k === form.trip_date && <span className="text-[10px] uppercase tracking-wider text-blue-700 dark:text-blue-300 font-semibold">Basistag</span>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={cancelConfirm} disabled={confirmBusy} data-testid="week-confirm-cancel">Abbrechen</Button>
            <Button onClick={confirmCreate} disabled={confirmBusy} data-testid="week-confirm-create">
              {confirmBusy ? "Wird angelegt…" : `${allTripDates.length} ${allTripDates.length === 1 ? "Fahrt" : "Fahrten"} anlegen`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy-to-other-days dialog */}
      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="week-copy-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarPlus className="h-5 w-5" /> Fahrt auf andere Tage kopieren</DialogTitle>
            <DialogDescription>
              {copySource ? (
                <>Quelle: <span className="font-medium text-foreground">{addr1(firstPickup(copySource))} → {addr1(firstDest(copySource))}</span>, {copySource.start_time}{copySource.end_time ? `–${copySource.end_time}` : ""} am {parseKey(copySource.trip_date).toLocaleDateString("de-DE")}.</>
              ) : "Wähle die Zieltage."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {/* Quick week selection */}
            <section>
              <h3 className="text-sm font-semibold heading mb-2 text-muted-foreground uppercase tracking-wider">Aktuelle Woche</h3>
              <div className="grid grid-cols-2 gap-2">
                {days.map((d, i) => {
                  const key = dateKey(d);
                  const isSource = copySource && key === copySource.trip_date;
                  return (
                    <label
                      key={key}
                      className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm ${isSource ? "opacity-50 cursor-not-allowed bg-muted/40" : "cursor-pointer hover:border-blue-400"}`}
                      data-testid={`week-copy-day-${key}`}
                    >
                      <Checkbox
                        checked={copyDates.includes(key)}
                        disabled={isSource}
                        onCheckedChange={() => !isSource && toggleCopyDate(key)}
                      />
                      <span className="tabular-nums">{WD[i]} <span className="text-muted-foreground">{shortDate(d)}</span>{isSource ? " (Quelle)" : ""}</span>
                    </label>
                  );
                })}
              </div>
            </section>

            {/* Arbitrary date selection via calendar */}
            <section>
              <h3 className="text-sm font-semibold heading mb-2 text-muted-foreground uppercase tracking-wider">Weitere Tage (Kalender)</h3>
              <div className="rounded-md border w-fit">
                <Calendar
                  mode="multiple"
                  selected={copyDates.map(parseKey)}
                  onSelect={(ds) => setCopyDates((ds || []).map(dateKey))}
                  data-testid="week-copy-calendar"
                />
              </div>
            </section>

            {/* Status reset toggle */}
            <section className="flex items-center justify-between rounded-md border px-3 py-2">
              <div>
                <Label className="text-sm">Status auf „Geplant" zurücksetzen</Label>
                <p className="text-xs text-muted-foreground">Aus, um den Status der Quelle ({STATUS_LABEL[copySource?.status] || "—"}) zu übernehmen.</p>
              </div>
              <Switch checked={copyReset} onCheckedChange={setCopyReset} data-testid="week-copy-reset-status" />
            </section>

            <p className="text-xs text-muted-foreground" data-testid="week-copy-count">
              {copyDates.filter((k) => k !== copySource?.trip_date).length} Zieltag(e) ausgewählt. Fahrer, Fahrzeug, Adressen und Zeiten werden mitkopiert.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setCopyOpen(false)}>Abbrechen</Button>
            <Button onClick={doCopy} disabled={copyBusy} data-testid="week-copy-confirm">
              <Copy className="h-4 w-4 mr-1" /> {copyBusy ? "Kopiere…" : "Kopieren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail modals (driver + vehicle) — globally accessible from any clickable name/plate */}
      <DriverDetailModal driver={detailDriver} open={!!detailDriver} onOpenChange={(o) => !o && setDetailDriverId(null)} />
      <VehicleDetailModal vehicle={detailVehicle} open={!!detailVehicle} onOpenChange={(o) => !o && setDetailVehicleId(null)} />
    </div>
  );
}

/* ===== Per-driver weekly summary (trip count + planned hours) ===== */
function WeekDriverSummary({ items, totalPlannedMin, totalTrips, onOpenDriver }) {
  const fmtH = (m) => {
    if (!m || m <= 0) return "0 h";
    const h = Math.floor(m / 60);
    const min = m % 60;
    return `${h}:${String(min).padStart(2, "0")} h`;
  };
  return (
    <Card className="p-3" data-testid="week-driver-summary">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
        <h2 className="text-sm font-semibold heading text-foreground/90 flex items-center gap-1.5">
          <Users className="h-4 w-4 text-blue-600" /> Fahrer-Übersicht der Woche
        </h2>
        <div className="text-xs text-muted-foreground tabular-nums" data-testid="week-driver-summary-totals">
          Gesamt: <span className="font-semibold text-foreground">{totalTrips}</span> Touren ·{" "}
          <span className="font-semibold text-foreground">{fmtH(totalPlannedMin)}</span> geplant
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">Keine aktiven Fahrer angelegt.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
          {items.map((d) => {
            const col = colorForDriverId(d.driverId);
            const empty = d.tripCount === 0;
            const clickable = !!d.driverId && !!onOpenDriver;
            return (
              <div
                key={d.driverId || "__unassigned__"}
                className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 ${empty ? "border-border bg-muted/30" : "border-border bg-card"}`}
                data-testid={`week-driver-summary-row-${d.driverId || "none"}`}
              >
                <span className={`inline-block h-3 w-3 shrink-0 rounded-sm ${col.solid} border ${col.border}`} />
                <div className="min-w-0 flex-1">
                  {clickable ? (
                    <button
                      type="button"
                      onClick={() => onOpenDriver(d.driverId)}
                      title={`${d.name} – Details anzeigen`}
                      className="text-left text-[13px] font-medium truncate w-full hover:text-blue-700 dark:hover:text-blue-300 hover:underline focus:outline-none focus-visible:underline"
                      data-testid={`week-driver-summary-name-${d.driverId}`}
                    >
                      {d.name}
                    </button>
                  ) : (
                    <div className={`text-[13px] font-medium truncate ${!d.driverId ? "italic text-muted-foreground" : ""}`} title={d.name}>
                      {d.name}
                    </div>
                  )}
                  <div className="text-[11px] text-muted-foreground tabular-nums">
                    <span data-testid={`week-driver-summary-trips-${d.driverId || "none"}`} className={`font-semibold ${empty ? "" : "text-foreground"}`}>{d.tripCount}</span>{" "}
                    {d.tripCount === 1 ? "Tour" : "Touren"} ·{" "}
                    <span data-testid={`week-driver-summary-hours-${d.driverId || "none"}`} className={`font-semibold ${empty ? "" : "text-foreground"}`}>{fmtH(d.minutes)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}


/* ===== List view (existing 7-column board) ===== */
function ListBoard({ days, byDay, isToday, drivers, vehicles, openCreate, openEdit, openCopy, patchTrip, canEdit, absenceFor, ABSENCE_LABEL }) {
  const dragIdRef = useRef(null);
  const dragFromRef = useRef(null);
  const [dragId, setDragId] = useState(null);
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  const startDrag = (id, key) => { dragIdRef.current = id; dragFromRef.current = key; setDragId(id); setDragFrom(key); };
  const clearDrag = () => { dragIdRef.current = null; dragFromRef.current = null; setDragId(null); setDragFrom(null); setDragOverKey(null); };

  const onDrop = (targetKey) => {
    const id = dragIdRef.current;
    const from = dragFromRef.current;
    clearDrag();
    if (id && from && from !== targetKey) {
      patchTrip(id, { trip_date: targetKey });
      toast.success("Fahrt verschoben");
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 gap-3">
      {days.map((d, i) => {
        const key = dateKey(d);
        const list = byDay[key] || [];
        const isDropTarget = dragOverKey === key && dragFrom !== key;
        return (
          <div
            key={key}
            className={`rounded-lg border ${isDropTarget ? "border-blue-500 ring-2 ring-blue-400/50" : isToday(d) ? "border-blue-400 dark:border-blue-600" : "border-border"} bg-card flex flex-col transition-shadow`}
            data-testid={`week-day-${key}`}
            onDragOver={canEdit ? (e) => { e.preventDefault(); if (dragOverKey !== key) setDragOverKey(key); } : undefined}
            onDragLeave={canEdit ? (e) => { if (e.currentTarget === e.target) setDragOverKey(null); } : undefined}
            onDrop={canEdit ? (e) => { e.preventDefault(); onDrop(key); } : undefined}
          >
            <div className={`px-3 py-2 border-b border-border flex items-center justify-between ${isToday(d) ? "bg-blue-50 dark:bg-blue-950/40" : "bg-muted/40"}`}>
              <div>
                <div className="text-sm font-semibold heading">{WD[i]}</div>
                <div className="text-xs text-muted-foreground tabular-nums">{shortDate(d)}{isToday(d) ? " · Heute" : ""}</div>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground tabular-nums">{list.length}</span>
                {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openCreate(d)} aria-label="Fahrt planen" data-testid={`week-add-${key}`}><Plus className="h-4 w-4" /></Button>}
              </div>
            </div>
            <div className="p-2 space-y-2 min-h-[80px] flex-1">
              {list.length === 0 && <div className="text-xs text-muted-foreground italic px-1 py-3 text-center">{isDropTarget ? "Hier ablegen" : "Keine Fahrten"}</div>}
              {list.map((t) => {
                const driverAbsent = t.driver_id ? absenceFor(t.driver_id, key) : null;
                return (
                <Card
                  key={t.id}
                  draggable={canEdit}
                  onDragStart={canEdit ? (e) => { startDrag(t.id, key); e.dataTransfer.effectAllowed = "move"; } : undefined}
                  onDragEnd={clearDrag}
                  className={`p-2 space-y-2 cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition-colors ${dragId === t.id ? "opacity-40" : ""} ${!t.driver_id ? "border-red-300 dark:border-red-800" : ""}`}
                  data-testid={`week-trip-card-${t.id}`}
                  onClick={() => openEdit(t)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold tabular-nums">{t.start_time}{t.end_time ? `–${t.end_time}` : ""}</span>
                    <div className="flex items-center gap-1">
                      <StatusBadge status={t.status} />
                      {canEdit && (
                        <Button
                          size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-foreground"
                          onClick={(e) => { e.stopPropagation(); openCopy(t); }}
                          aria-label="Auf andere Tage kopieren"
                          data-testid={`week-trip-copy-${t.id}`}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {driverAbsent && (
                    <div className="flex items-center gap-1 rounded bg-amber-100 dark:bg-amber-950/50 text-amber-800 dark:text-amber-200 px-1.5 py-0.5 text-[11px] font-medium" data-testid={`week-trip-absence-${t.id}`}>
                      <AlertTriangle className="h-3 w-3 shrink-0" /> Fahrer im {ABSENCE_LABEL[driverAbsent]}
                    </div>
                  )}
                  <div className="text-xs space-y-0.5">
                    <div className="flex items-start gap-1"><MapPin className="h-3 w-3 mt-0.5 text-emerald-600 shrink-0" /><span className="truncate" title={addr1(firstPickup(t))}>{addr1(firstPickup(t))}{(t.pickups?.length || 0) > 1 ? ` (+${t.pickups.length - 1})` : ""}</span></div>
                    <div className="flex items-start gap-1"><Flag className="h-3 w-3 mt-0.5 text-blue-600 shrink-0" /><span className="truncate" title={addr1(firstDest(t))}>{addr1(firstDest(t))}{(t.destinations?.length || 0) > 1 ? ` (+${t.destinations.length - 1})` : ""}</span></div>
                    {t.line_label && (
                      <div className="flex items-start gap-1" data-testid={`week-trip-line-${t.id}`}><Bus className="h-3 w-3 mt-0.5 text-violet-600 shrink-0" /><span className="truncate" title={t.line_label}>{t.line_label}</span></div>
                    )}
                    {t.notes && (
                      <div className="flex items-start gap-1" data-testid={`week-trip-notes-${t.id}`}><StickyNote className="h-3 w-3 mt-0.5 text-amber-600 shrink-0" /><span className="truncate" title={t.notes}>{t.notes}</span></div>
                    )}
                  </div>
                  <div className="space-y-1.5" onClick={(e) => e.stopPropagation()}>
                    <Select value={t.driver_id || "none"} onValueChange={(v) => patchTrip(t.id, { driver_id: v === "none" ? null : v })}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`week-trip-driver-${t.id}`}>
                        <span className="inline-flex items-center gap-1 truncate"><User className="h-3 w-3 shrink-0" /><SelectValue placeholder="Fahrer wählen" /></span>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— kein Fahrer —</SelectItem>
                        {drivers.filter((dr) => dr.active).map((dr) => {
                          const ab = absenceFor(dr.id, key);
                          return <SelectItem key={dr.id} value={dr.id} className={ab ? "text-amber-600 dark:text-amber-400" : ""}>{dr.firstname} {dr.lastname}{ab ? ` · ${ABSENCE_LABEL[ab]}` : ""}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                    <Select value={t.vehicle_id || "none"} onValueChange={(v) => patchTrip(t.id, { vehicle_id: v === "none" ? null : v })}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`week-trip-vehicle-${t.id}`}>
                        <span className="inline-flex items-center gap-1 truncate"><Truck className="h-3 w-3 shrink-0" /><SelectValue placeholder="Fahrzeug wählen" /></span>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— kein Fahrzeug —</SelectItem>
                        {vehicles.filter((v) => v.active).map((v) => <SelectItem key={v.id} value={v.id}>{v.license_plate}{v.vehicle_type ? ` · ${v.vehicle_type}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={t.status} onValueChange={(v) => patchTrip(t.id, { status: v })}>
                      <SelectTrigger className="h-7 text-xs" data-testid={`week-trip-status-${t.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </Card>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ===== Timeline view (Gantt-style 0–24h per day) ===== */
function TimelineBoard({ days, byDay, isToday, openCreate, openEdit, patchTrip, canEdit, snapMin, zoomMin, openDriver }) {
  const DAY_MIN = 24 * 60;
  const ROW_HEIGHT = 34;   // px per bar row
  const ROW_GAP = 4;
  const HOURS = Array.from({ length: 25 }, (_, i) => i); // 0..24
  const LABEL_W = 260;     // day label (130) + driver label (130)
  // Horizontal scale: chosen visible span (zoomMin) ≈ what fits in a ~1000px viewport.
  const timeWidthPx = Math.round((1440 / zoomMin) * 1000);
  const innerMinWidth = LABEL_W + timeWidthPx;
  const [dragOverDay, setDragOverDay] = useState(null);
  const [dragOverLane, setDragOverLane] = useState(null); // `${dayKey}|${driverId|__unassigned__}` while cross-lane drag in progress
  // Refs to each day's track element so a bar drag can detect the day under the pointer
  const trackRefs = useRef({});

  // Horizontal scroll/pan control (to page through the day when zoomed in)
  const scrollRef = useRef(null);
  const [scrollState, setScrollState] = useState({ canLeft: false, canRight: false });
  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setScrollState({ canLeft: el.scrollLeft > 2, canRight: el.scrollLeft < max - 2 });
  };
  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => { el.removeEventListener("scroll", updateScrollState); window.removeEventListener("resize", updateScrollState); };
  }, [zoomMin, days]);
  const scrollByPage = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = Math.max(200, el.clientWidth - LABEL_W);
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };
  const showNav = zoomMin < 1440;

  // Assign trips to non-overlapping rows per day (lane packing)
  const layoutForDay = (list) => {
    const sorted = [...list].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
    const lanes = []; // lanes[i] = last end minute used
    const placed = [];
    for (const t of sorted) {
      const s = toMinutes(t.start_time);
      let e = toMinutes(t.end_time);
      if (s == null) continue;
      if (e == null || e <= s) e = Math.min(DAY_MIN, s + 60); // default 60 min if no end
      let laneIdx = lanes.findIndex((endM) => endM <= s);
      if (laneIdx === -1) { laneIdx = lanes.length; lanes.push(e); }
      else { lanes[laneIdx] = e; }
      placed.push({ trip: t, s, e, lane: laneIdx });
    }
    return { placed, laneCount: lanes.length };
  };

  return (
    <Card ref={scrollRef} className="p-3 overflow-auto max-h-[75vh]" data-testid="week-timeline">
      <div style={{ minWidth: innerMinWidth }}>
        {/* Hour header with inline navigation buttons (pinned to visible edges while scrolling) */}
        <div className="flex items-end pl-[260px] pr-2 pb-1 border-b border-border mb-2 select-none sticky top-0 z-30 bg-card">
          <div className="relative w-full h-7">
            {HOURS.map((h) => (
              <div key={h} className="absolute top-1 -translate-x-1/2 text-[10px] tabular-nums text-muted-foreground" style={{ left: `${(h / 24) * 100}%` }}>
                {h.toString().padStart(2, "0")}
              </div>
            ))}
            {showNav && (
              <Button
                type="button" size="icon" variant="secondary"
                onClick={() => scrollByPage(-1)} disabled={!scrollState.canLeft}
                className="sticky float-left left-0 z-30 h-7 w-7 rounded-full shadow border border-border bg-card disabled:opacity-30"
                aria-label="Frühere Uhrzeiten" data-testid="timeline-scroll-left"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
            {showNav && (
              <Button
                type="button" size="icon" variant="secondary"
                onClick={() => scrollByPage(1)} disabled={!scrollState.canRight}
                className="sticky float-right right-0 z-30 h-7 w-7 rounded-full shadow border border-border bg-card disabled:opacity-30"
                aria-label="Spätere Uhrzeiten" data-testid="timeline-scroll-right"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Hint + Legend (top row) */}
        <div className="flex items-center justify-between gap-4 mb-2 px-1 flex-wrap">
          {canEdit ? (
            <span className="text-[11px] text-muted-foreground flex-1 min-w-[240px]">
              Tipp: Jeder Fahrer bekommt pro Tag eine eigene Spur. Balken ziehen verschiebt die Fahrt ({snapMin}-Min-Raster) – auch in einen anderen Tag oder eine andere Fahrer-Spur. Linke/rechte Kante zieht Start- bzw. Endzeit. Klick öffnet die Bearbeitung.
            </span>
          ) : <span className="flex-1" />}
          <Legend trips={Object.values(byDay).flat()} />
        </div>

        {/* Day rows */}
        <div className="space-y-2">
          {days.map((d, dayIdx) => (
            <TimelineRow
              key={dateKey(d)}
              date={d}
              dayIdx={dayIdx}
              list={byDay[dateKey(d)] || []}
              layoutForDay={layoutForDay}
              today={isToday(d)}
              openCreate={openCreate}
              openEdit={openEdit}
              patchTrip={patchTrip}
              canEdit={canEdit}
              snapMin={snapMin}
              DAY_MIN={DAY_MIN}
              ROW_HEIGHT={ROW_HEIGHT}
              ROW_GAP={ROW_GAP}
              HOURS={HOURS}
              dragOverDay={dragOverDay}
              setDragOverDay={setDragOverDay}
              dragOverLane={dragOverLane}
              setDragOverLane={setDragOverLane}
              trackRefs={trackRefs}
              openDriver={openDriver}
            />
          ))}
        </div>
      </div>
    </Card>
  );
}

/* ===== Single day row — split into one sub-row per driver ===== */
function TimelineRow({ date, dayIdx, list, layoutForDay, today, openCreate, openEdit, patchTrip, canEdit, snapMin, DAY_MIN, ROW_HEIGHT, ROW_GAP, HOURS, dragOverDay, setDragOverDay, dragOverLane, setDragOverLane, trackRefs, openDriver }) {
  const key = dateKey(date);
  const DRIVER_LABEL_W = 130;

  // Group trips by driver_id (unassigned grouped as "ohne Fahrer")
  const groups = useMemo(() => {
    const map = new Map();
    for (const t of list) {
      const did = t.driver_id || "__unassigned__";
      if (!map.has(did)) {
        map.set(did, {
          key: did,
          driverId: t.driver_id || null,
          name: t.driver_name || "ohne Fahrer",
          trips: [],
        });
      }
      map.get(did).trips.push(t);
    }
    const arr = Array.from(map.values());
    arr.sort((a, b) => {
      if (!a.driverId && b.driverId) return 1;   // "ohne Fahrer" zuletzt
      if (a.driverId && !b.driverId) return -1;
      return (a.name || "").localeCompare(b.name || "");
    });
    return arr;
  }, [list]);

  // Find driver- and/or vehicle-conflicts within this day (across all sub-rows)
  const findConflicts = (excludeTripId, driverId, vehicleId, s, e) => {
    let driver = null;
    let vehicle = null;
    for (const t of list) {
      if (t.id === excludeTripId) continue;
      const ts = toMinutes(t.start_time);
      if (ts == null) continue;
      let te = toMinutes(t.end_time);
      if (te == null || te <= ts) te = Math.min(DAY_MIN, ts + 60);
      if (!(s < te && e > ts)) continue;
      if (driverId && !driver && t.driver_id === driverId) driver = t;
      if (vehicleId && !vehicle && t.vehicle_id === vehicleId) vehicle = t;
      if (driver && vehicle) break;
    }
    return (driver || vehicle) ? { driver, vehicle } : null;
  };

  // Empty day: still render a single empty placeholder lane so the day-key zone exists
  const lanes = groups.length === 0 ? [{ key: "__empty__", driverId: null, name: "—", trips: [] }] : groups;

  return (
    <div
      className={`rounded-md border transition-colors ${dragOverDay === key ? "border-blue-500 ring-2 ring-blue-400/50 bg-blue-50/60 dark:bg-blue-950/50" : today ? "border-blue-400 dark:border-blue-600 bg-blue-50/40 dark:bg-blue-950/30" : "border-border bg-card"}`}
      data-testid={`week-timeline-day-${key}`}
      data-day-key={key}
    >
      <div className="flex items-stretch gap-0">
        {/* Day label (sticky on horizontal scroll) */}
        <div className="w-[130px] shrink-0 px-3 py-2 border-r border-border flex flex-col justify-center sticky left-0 z-20 bg-card">
          <div className="text-sm font-semibold heading">{WD[dayIdx]}</div>
          <div className="text-xs text-muted-foreground tabular-nums">{shortDate(date)}{today ? " · Heute" : ""}</div>
          <div className="text-[11px] text-muted-foreground mt-1">{list.length} Fahrt{list.length === 1 ? "" : "en"}</div>
          {canEdit && (
            <Button size="sm" variant="ghost" className="h-6 px-1 mt-1 self-start text-xs" onClick={() => openCreate(date)} data-testid={`week-timeline-add-${key}`}>
              <Plus className="h-3 w-3 mr-1" /> Neu
            </Button>
          )}
        </div>

        {/* Driver sub-rows */}
        <div className="flex-1 min-w-0">
          {lanes.map((g, idx) => (
            <DriverLane
              key={g.key}
              group={g}
              isFirst={idx === 0}
              dayKey={key}
              labelW={DRIVER_LABEL_W}
              layoutForDay={layoutForDay}
              openEdit={openEdit}
              patchTrip={patchTrip}
              canEdit={canEdit}
              snapMin={snapMin}
              DAY_MIN={DAY_MIN}
              ROW_HEIGHT={ROW_HEIGHT}
              ROW_GAP={ROW_GAP}
              HOURS={HOURS}
              setDragOverDay={setDragOverDay}
              dragOverLane={dragOverLane}
              setDragOverLane={setDragOverLane}
              trackRefs={trackRefs}
              findConflicts={findConflicts}
              openDriver={openDriver}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ===== Driver lane (one bar track for a specific driver on a specific day) ===== */
function DriverLane({ group, isFirst, dayKey, labelW, layoutForDay, openEdit, patchTrip, canEdit, snapMin, DAY_MIN, ROW_HEIGHT, ROW_GAP, HOURS, setDragOverDay, dragOverLane, setDragOverLane, trackRefs, findConflicts, openDriver }) {
  const trackRef = useRef(null);
  const { placed, laneCount } = layoutForDay(group.trips);
  const rows = Math.max(1, laneCount);
  const trackHeight = rows * ROW_HEIGHT + (rows - 1) * ROW_GAP;

  // Drop-target identity for cross-lane drag (empty placeholder lane does NOT act as a target)
  const isPlaceholder = group.key === "__empty__";
  const laneDriverAttr = isPlaceholder ? null : (group.driverId || "__unassigned__");
  const laneKey = laneDriverAttr ? `${dayKey}|${laneDriverAttr}` : null;
  const isDropTarget = laneKey && dragOverLane === laneKey;

  // Register this lane's track under the day key (multiple lanes per day share width — last write wins, which is fine).
  useEffect(() => {
    if (!trackRefs) return undefined;
    trackRefs.current[dayKey] = trackRef;
    return () => { if (trackRefs.current[dayKey] === trackRef) delete trackRefs.current[dayKey]; };
  }, [dayKey, trackRefs]);

  return (
    <div
      className={`flex items-stretch transition-colors ${!isFirst ? "border-t border-border/60" : ""} ${isDropTarget ? "bg-blue-100/70 dark:bg-blue-900/40 ring-2 ring-inset ring-blue-500" : ""}`}
      {...(laneDriverAttr ? { "data-driver-id": laneDriverAttr } : {})}
    >
      {/* Driver label (sticky on horizontal scroll, right after the day label) */}
      <div
        className="shrink-0 px-2 py-1 border-r border-border/60 flex items-center sticky z-20 bg-card"
        style={{ width: labelW, left: 130 }}
        title={group.name}
      >
        {group.driverId && openDriver ? (
          <button
            type="button"
            onClick={() => openDriver(group.driverId)}
            className="truncate text-[12px] font-medium text-left text-foreground/90 hover:text-blue-700 dark:hover:text-blue-300 hover:underline focus:outline-none focus-visible:underline"
            data-testid={`week-timeline-driver-${dayKey}-${group.driverId}`}
          >
            {group.name}
          </button>
        ) : (
          <span className={`truncate text-[12px] font-medium ${!group.driverId ? "italic text-muted-foreground" : "text-foreground/90"}`} data-testid={`week-timeline-driver-${dayKey}-${group.driverId || "none"}`}>
            {group.name}
          </span>
        )}
      </div>

      {/* Bar track for this driver */}
      <div className="relative flex-1 py-1.5 pr-2 min-w-0">
        {/* hour grid lines */}
        <div className="absolute inset-y-1.5 left-0 right-2 pointer-events-none">
          {HOURS.map((h) => (
            <div
              key={h}
              className={`absolute top-0 bottom-0 ${h % 6 === 0 ? "border-l border-border" : "border-l border-border/40"}`}
              style={{ left: `${(h / 24) * 100}%` }}
            />
          ))}
        </div>

        <div ref={trackRef} data-day-track className="relative" style={{ height: trackHeight }}>
          {placed.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-[11px] text-muted-foreground/70 italic">—</div>
          )}
          {placed.map(({ trip, s, e, lane }) => (
            <TimelineBar
              key={trip.id}
              trip={trip}
              s={s}
              e={e}
              top={lane * (ROW_HEIGHT + ROW_GAP)}
              height={ROW_HEIGHT}
              trackRef={trackRef}
              dayKey={dayKey}
              laneDriverId={group.driverId || null}
              trackRefs={trackRefs}
              setDragOverDay={setDragOverDay}
              setDragOverLane={setDragOverLane}
              DAY_MIN={DAY_MIN}
              snapMin={snapMin}
              canEdit={canEdit}
              findConflicts={findConflicts}
              onClick={() => openEdit(trip)}
              onCommit={(patch) => patchTrip(trip.id, patch)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ===== Draggable / resizable bar ===== */
function TimelineBar({ trip, s, e, top, height, trackRef, dayKey, laneDriverId, trackRefs, setDragOverDay, setDragOverLane, DAY_MIN, snapMin, canEdit, findConflicts, onClick, onCommit }) {
  const [preview, setPreview] = useState(null); // { s, e, dayKey } during drag
  const dragRef = useRef(null);
  const movedRef = useRef(false);

  const ps = preview ? preview.s : s;
  const pe = preview ? preview.e : e;
  const left = (ps / DAY_MIN) * 100;
  const width = Math.max(0.8, ((pe - ps) / DAY_MIN) * 100);
  const col = colorForTrip(trip);

  // Conflict detection (preview or static state)
  const conflicts = findConflicts ? findConflicts(trip.id, trip.driver_id, trip.vehicle_id, ps, pe) : null;
  const hasDriver = !!conflicts?.driver;
  const hasVehicle = !!conflicts?.vehicle;
  const conflictKind = hasDriver && hasVehicle ? "both" : hasDriver ? "driver" : hasVehicle ? "vehicle" : "false";
  // Driver conflict is more critical → red. Vehicle-only → amber.
  const conflictRing = hasDriver
    ? "ring-2 ring-red-500 ring-offset-1 ring-offset-background animate-pulse"
    : hasVehicle
    ? "ring-2 ring-amber-500 ring-offset-1 ring-offset-background animate-pulse"
    : "";
  const minDur = snapMin || 15;

  const fmt = (m) => {
    const cm = Math.min(DAY_MIN - 1, Math.max(0, m)); // 0..1439 — keep HTML time-input compatible
    return `${String(Math.floor(cm / 60)).padStart(2, "0")}:${String(cm % 60).padStart(2, "0")}`;
  };
  const snap = (m) => Math.round(m / minDur) * minDur;

  // Find day-key & lane driver-id under the pointer by walking up from elementFromPoint
  const targetAtPoint = (clientX, clientY) => {
    const el = document.elementFromPoint(clientX, clientY);
    if (!el) return { dayKey: null, driverId: null };
    const dayEl = el.closest("[data-day-key]");
    const laneEl = el.closest("[data-driver-id]");
    return {
      dayKey: dayEl ? dayEl.getAttribute("data-day-key") : null,
      driverId: laneEl ? laneEl.getAttribute("data-driver-id") : null,
    };
  };

  const handlePointerDown = (ev) => {
    if (!canEdit) return;
    if (ev.button !== undefined && ev.button !== 0) return; // primary button only
    const handle = ev.target?.dataset?.handle;
    const mode = handle === "start" ? "resize-start" : handle === "end" ? "resize-end" : "move";
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return;
    ev.preventDefault();
    dragRef.current = {
      mode,
      startX: ev.clientX,
      startY: ev.clientY,
      origS: s,
      origE: e,
      trackW: rect.width,
      targetDay: dayKey,
      targetDriver: laneDriverId || null, // source lane's driver as starting point
    };
    movedRef.current = false;
    try { ev.currentTarget.setPointerCapture(ev.pointerId); } catch { /* ignore */ }
  };

  const handlePointerMove = (ev) => {
    const d = dragRef.current;
    if (!d) return;
    const dxPx = ev.clientX - d.startX;
    const dyPx = ev.clientY - d.startY;
    if (Math.abs(dxPx) > 3 || Math.abs(dyPx) > 3) movedRef.current = true;

    let trackW = d.trackW;
    let targetDay = d.targetDay;
    let targetDriver = d.targetDriver;
    // For "move" mode allow cross-day AND cross-lane. Use target track width for px→min conversion if known.
    if (d.mode === "move") {
      const hit = targetAtPoint(ev.clientX, ev.clientY);
      if (hit.dayKey && trackRefs?.current?.[hit.dayKey]?.current) {
        targetDay = hit.dayKey;
        const r = trackRefs.current[hit.dayKey].current.getBoundingClientRect();
        if (r.width > 0) trackW = r.width;
      }
      // Only update target driver when the pointer is actually over a lane with a driver-id attribute
      // (placeholder lanes have none, in which case we keep the current driver).
      if (hit.driverId !== null) targetDriver = hit.driverId;
    }

    const dayChangedNow = targetDay !== d.targetDay;
    const driverChangedNow = targetDriver !== d.targetDriver;
    if (dayChangedNow || driverChangedNow) {
      d.targetDay = targetDay;
      d.targetDriver = targetDriver;
      d.trackW = trackW;
      setDragOverDay && setDragOverDay(targetDay !== dayKey ? targetDay : null);
      setDragOverLane && setDragOverLane(
        targetDriver && (targetDay !== dayKey || targetDriver !== (laneDriverId || "__unassigned__"))
          ? `${targetDay}|${targetDriver}`
          : null
      );
    }

    const dxMin = snap((dxPx / d.trackW) * DAY_MIN);
    let ns = d.origS;
    let ne = d.origE;
    if (d.mode === "move") {
      const dur = d.origE - d.origS;
      ns = Math.max(0, Math.min(DAY_MIN - dur, d.origS + dxMin));
      ne = ns + dur;
    } else if (d.mode === "resize-start") {
      ns = Math.max(0, Math.min(d.origE - minDur, d.origS + dxMin));
    } else if (d.mode === "resize-end") {
      ne = Math.max(d.origS + minDur, Math.min(DAY_MIN, d.origE + dxMin));
    }
    setPreview({ s: ns, e: ne, dayKey: d.targetDay });
  };

  const handlePointerUp = (ev) => {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    setDragOverDay && setDragOverDay(null);
    setDragOverLane && setDragOverLane(null);
    try { ev.currentTarget.releasePointerCapture(ev.pointerId); } catch { /* ignore */ }
    const dayChanged = d.mode === "move" && d.targetDay && d.targetDay !== dayKey;
    // Did the user drop on a different driver lane? (laneDriverId is the source lane's driver; "__unassigned__" for ohne-Fahrer lane)
    const sourceLaneDriver = laneDriverId || "__unassigned__";
    const driverChanged = d.mode === "move" && d.targetDriver && d.targetDriver !== sourceLaneDriver;
    if (movedRef.current && preview && (preview.s !== s || preview.e !== e || dayChanged || driverChanged)) {
      // Conflict check at commit time — only meaningful when neither day nor driver changed
      const conf = (!dayChanged && !driverChanged && findConflicts) ? findConflicts(trip.id, trip.driver_id, trip.vehicle_id, preview.s, preview.e) : null;
      if (conf) {
        const parts = [];
        if (conf.driver) parts.push(`Fahrer ${trip.driver_name || ""} ist bereits ${conf.driver.start_time}${conf.driver.end_time ? "–" + conf.driver.end_time : ""} eingeteilt`);
        if (conf.vehicle) parts.push(`Fahrzeug ${trip.license_plate || ""} ist bereits ${conf.vehicle.start_time}${conf.vehicle.end_time ? "–" + conf.vehicle.end_time : ""} eingeteilt`);
        const msg = `Konflikt:\n• ${parts.join("\n• ")}\n\nTrotzdem auf ${fmt(preview.s)}–${fmt(preview.e)} verschieben?`;
        if (!window.confirm(msg)) {
          setPreview(null);
          return;
        }
      }
      const patch = { start_time: fmt(preview.s), end_time: fmt(preview.e) };
      if (dayChanged) patch.trip_date = d.targetDay;
      if (driverChanged) {
        patch.driver_id = d.targetDriver === "__unassigned__" ? null : d.targetDriver;
      }
      onCommit(patch);
    }
    setPreview(null);
  };

  const handleClick = (ev) => {
    if (movedRef.current) {
      ev.preventDefault();
      ev.stopPropagation();
      movedRef.current = false;
      return;
    }
    onClick();
  };

  const cursor = canEdit ? (dragRef.current ? "grabbing" : "grab") : "pointer";
  const previewTitle = preview ? `${fmt(preview.s)}–${fmt(preview.e)}` : `${fmt(s)}${e > s ? "–" + fmt(e) : ""}`;

  return (
    <div
      role="button"
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onClick={handleClick}
      onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); onClick(); } }}
      className={`absolute select-none touch-none rounded-md border-2 ${col.bg} ${col.border} ${col.text} px-2 text-left text-[11px] leading-tight overflow-hidden hover:brightness-110 hover:shadow-md transition-shadow focus:outline-none focus:ring-2 focus:ring-blue-500 ${trip.status === "storniert" ? "opacity-50 line-through" : ""} ${conflictRing}`}
      style={{ left: `${left}%`, width: `${width}%`, top, height, cursor }}
      title={`${previewTitle} · ${trip.driver_name || "ohne Fahrer"}${trip.license_plate ? " · " + trip.license_plate : ""}${hasDriver ? "\n⚠ Fahrer-Konflikt mit " + conflicts.driver.start_time + (conflicts.driver.end_time ? "–" + conflicts.driver.end_time : "") : ""}${hasVehicle ? "\n⚠ Fahrzeug-Konflikt mit " + conflicts.vehicle.start_time + (conflicts.vehicle.end_time ? "–" + conflicts.vehicle.end_time : "") : ""}\n${addr1(firstPickup(trip))} → ${addr1(firstDest(trip))}`}
      data-testid={`week-timeline-bar-${trip.id}`}
      data-conflict={conflictKind}
    >
      {canEdit && (
        <>
          <div
            data-handle="start"
            className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-black/20 dark:hover:bg-white/20 rounded-l-md"
            data-testid={`week-timeline-bar-${trip.id}-resize-start`}
          />
          <div
            data-handle="end"
            className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-black/20 dark:hover:bg-white/20 rounded-r-md"
            data-testid={`week-timeline-bar-${trip.id}-resize-end`}
          />
        </>
      )}
      <div className="flex items-center gap-1 font-semibold tabular-nums truncate pointer-events-none">
        <span>{fmt(ps)}{pe > ps ? `–${fmt(pe)}` : ""}</span>
        <span className="truncate font-normal opacity-95">· {trip.driver_name || "ohne Fahrer"}</span>
      </div>
      <div className="truncate opacity-90 pointer-events-none">
        {trip.license_plate ? `${trip.line_label ? trip.line_label + " · " : ""}${trip.license_plate}` : (trip.line_label || "kein Fahrzeug")}
      </div>
    </div>
  );
}

function Legend({ trips }) {
  // Build unique drivers seen in trips
  const seen = new Map();
  trips.forEach((t) => {
    if (!t.driver_id) {
      seen.set("__none__", { name: "ohne Fahrer", color: UNASSIGNED_COLOR });
    } else if (!seen.has(t.driver_id)) {
      seen.set(t.driver_id, { name: t.driver_name || "Fahrer", color: colorForTrip(t) });
    }
  });
  const items = Array.from(seen.values());
  return (
    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-xs" data-testid="week-timeline-legend">
      <span className="text-muted-foreground font-medium">Legende:</span>
      {items.map((it, i) => (
        <span key={i} className="inline-flex items-center gap-1.5">
          <span className={`inline-block h-3 w-3 rounded ${it.color.solid} border ${it.color.border}`} />
          <span>{it.name}</span>
        </span>
      ))}
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded-sm ring-2 ring-red-500 bg-transparent" />
        <span>Fahrer-Konflikt</span>
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded-sm ring-2 ring-amber-500 bg-transparent" />
        <span>Fahrzeug-Konflikt</span>
      </span>
    </div>
  );
}
